/**
 * VehicleTransferDialog — Transferência de propriedade do veículo
 *
 * Reaproveita 100% a estrutura de backend já existente:
 *  - RPC `transfer_vehicle_ownership` (ledger SCD Tipo 2 `vehicle_ownerships`)
 *  - `ContactSelector` para escolher o novo proprietário (traz o cliente vinculado)
 *
 * Fluxo:
 *  1. Usuário escolhe o novo contato/cliente, motivo e data de efeito.
 *  2. Se existirem documentos financeiros emitidos após a data de efeito,
 *     o backend recusa a operação e o modal exibe uma confirmação extra
 *     que reenvia a chamada com `p_force = true`.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-19 v1.0.0 - Criação inicial
 */
import { useEffect, useState } from 'react';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { useTransferVehicle } from '@/hooks/useVehicleOwnership';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

interface VehicleTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleId: string;
  /** Placa exibida no cabeçalho */
  plate?: string | null;
  /** Marca · modelo · ano */
  subtitle?: string | null;
  /** Proprietário atual (somente leitura) */
  currentOwnerName?: string | null;
  /** Callback após transferência concluída */
  onTransferred?: () => void;
}

/** Converte um Date local para o formato aceito pelo input datetime-local */
const toLocalInputValue = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

/** Detecta a recusa por documentos financeiros posteriores à data de efeito */
const isBilledConflict = (message?: string) =>
  !!message && message.toLowerCase().includes('documento(s) financeiro(s)');

export function VehicleTransferDialog({
  open,
  onOpenChange,
  vehicleId,
  plate,
  subtitle,
  currentOwnerName,
  onTransferred,
}: VehicleTransferDialogProps) {
  const [contactId, setContactId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [effectiveAt, setEffectiveAt] = useState(() => toLocalInputValue(new Date()));
  /** Mensagem de conflito que exige confirmação (p_force) */
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);

  const transfer = useTransferVehicle();

  // Reseta o formulário sempre que o modal é aberto
  useEffect(() => {
    if (open) {
      setContactId(null);
      setReason('');
      setEffectiveAt(toLocalInputValue(new Date()));
      setConfirmMessage(null);
    }
  }, [open]);

  /** Busca o cliente vinculado ao contato escolhido (para enviar customer_id) */
  const { data: selectedContact } = useQuery({
    queryKey: ['contact-with-customer', contactId],
    queryFn: async () => {
      if (!contactId) return null;
      const { data, error } = await supabase.rpc('search_contacts_for_selector', {
        p_contact_id: contactId,
      });
      if (error || !data || data.length === 0) return null;
      return data[0] as { id: string; name: string | null; customer_id: string | null; customer_name: string | null };
    },
    enabled: !!contactId,
  });

  /** Executa a transferência (com ou sem confirmação forçada) */
  const runTransfer = (force: boolean) => {
    if (!contactId) {
      toast.error('Selecione o novo proprietário do veículo');
      return;
    }

    transfer.mutate(
      {
        vehicleId,
        newContactId: contactId,
        newCustomerId: selectedContact?.customer_id ?? null,
        reason: reason || null,
        effectiveAt: effectiveAt ? new Date(effectiveAt).toISOString() : null,
        force,
      },
      {
        onSuccess: () => {
          toast.success('Veículo transferido com sucesso');
          onTransferred?.();
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const message = error instanceof Error ? error.message : String(error);
          if (!force && isBilledConflict(message)) {
            // Segundo passo: pede confirmação explícita do usuário
            setConfirmMessage(message);
            return;
          }
          toast.error(message || 'Não foi possível transferir o veículo');
        },
      },
    );
  };

  const newOwnerLabel =
    selectedContact?.customer_name || selectedContact?.name || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowLeftRight className="h-4 w-4 text-primary" />
            Transferir veículo
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-xs">
            {plate && (
              <Badge variant="outline" className="font-mono text-[11px]">
                {plate}
              </Badge>
            )}
            <span className="truncate">{subtitle || 'Veículo'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Proprietário atual (somente leitura) */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Proprietário atual</Label>
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
              {currentOwnerName || <span className="text-muted-foreground">Avulso</span>}
            </div>
          </div>

          {/* Novo proprietário */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Novo proprietário</Label>
            <ContactSelector
              value={contactId}
              onSelect={setContactId}
              placeholder="Buscar cliente ou contato..."
            />
            {newOwnerLabel && (
              <p className="text-[11px] text-muted-foreground">
                Será vinculado a: <span className="font-medium">{newOwnerLabel}</span>
              </p>
            )}
          </div>

          {/* Data de efeito */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Data de efeito</Label>
            <Input
              type="datetime-local"
              value={effectiveAt}
              max={toLocalInputValue(new Date())}
              onChange={(e) => setEffectiveAt(e.target.value)}
              className="h-9"
            />
            <p className="text-[11px] text-muted-foreground">
              Documentos anteriores a esta data permanecem com o proprietário antigo.
            </p>
          </div>

          {/* Motivo */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Motivo (opcional)</Label>
            <Textarea
              value={reason}
              maxLength={500}
              rows={3}
              placeholder="Ex.: venda do veículo, troca de titularidade..."
              onChange={(e) => setReason(e.target.value)}
              className="resize-none text-sm"
            />
            <p className="text-right text-[10px] text-muted-foreground">{reason.length}/500</p>
          </div>

          {/* Confirmação extra quando há documentos financeiros posteriores */}
          {confirmMessage && (
            <InlineNotice variant="warning">
              <div className="space-y-1">
                <p className="font-medium">Confirmação necessária</p>
                <p className="text-xs text-muted-foreground">{confirmMessage}</p>
              </div>
            </InlineNotice>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={transfer.isPending}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!contactId || transfer.isPending}
            onClick={() => runTransfer(!!confirmMessage)}
          >
            {transfer.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {confirmMessage ? 'Confirmar mesmo assim' : 'Transferir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
